import { IShowable } from "../../interfaces/IShowable";
import { ScreenManager, ScreenManagerEvents } from "../../managers/ScreenManager";
import { CardData } from "../../types/types";
import { ScreenOrientationType } from "../../utils/GetOrientation";
import { GetUniqueNumbersFromInterval } from "../../utils/GetUniqueNumbersFromInterval";
import { GetWindowSize } from "../../utils/GetWindowSize";
import { RectSize } from "../../utils/RectSize";
import { Card, CardEffect } from "../card/Card";
import { LoaderFileKeys } from "../constant/AppConstants";
import { AppDebug } from "../debug/AppDebug";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { AppSettings } from "../managers/AppSettings";
import { CardsManager } from "../managers/CardsManager";
import { LayoutDataManager } from "../managers/LayoutDataManager";
import { TableScreen } from "../screen/TableScreen";
import { Player } from "./Player";

export type PlayerProps = {
    dX: number;
    dY: number;
    facing: Phaser.Geom.Point;
    cardsContainer: {
        portrait: {
            /**
             * cardsContainer scale for portrait orientation
             */
            scale: number;
            /**
             * cardsContainer scale bias for portrait orientation
             */
            bias: number;
            screenEdgeOffset: number;
        },
        landscape: {
            /**
             * cardsContainer scale for landscape orientation
             */
            scale: number;
            /**
             * cardsContainer scale bias for landscape orientation
             */
            bias: number;
            screenEdgeOffset: number;
        }
        frontFacing: boolean;
    };
    avatarContainer: {
        portrait: {
            /**
             * avatar base scale
             */
            scale: number;
            /**
             * avatar scale bias
             */
            bias: number;
            screenEdgeOffset: number;
        },
        landscape: {
            scale: number;
            bias: number;
            screenEdgeOffset: number;
        },
    };
    avatar: {
        portrait: {
            scale: number;
        },
        landscape: {
            scale: number;
        }
    };
    acceptorBubble?: {
        portrait: {
            scale: number;
        },
        landscape: {
            scale: number;
        }
    };
    speechBubble: {
        portrait: {
            /**
             * Facing direction of bubble in portrait mode. This is where the bubble springs out from during fadeIn
             */
            facing: Phaser.Geom.Point;
            /**
             * As bubble's content grows, it will expand towards this (horizontal) direction.
             * 1: expands to the right
             * -1: expands to the left
             * 0: does not horizontally expand, aligns to horizontal center.
             * NOTE: for North|South Players / bubbles that have their @see facing vertical (y:-1|1) in Portrait mode, this can be skipped. Such bubble will always expend in its facing direction.
             */
            contentGrowthX?: number;
        };
        landscape: {
            /**
             * Facing direction of bubble in portrait mode. This is where the bubble springs out from during fadeIn
             */
            facing: Phaser.Geom.Point;
            /**
             * As bubble's content grows, it will expand towards this (horizontal) direction.
             * 1: expands to the right
             * -1: expands to the left
             * 0: does not horizontally expand, aligns to horizontal center.
             * NOTE: for East|West Players /  bubbles that have their @see facing horizontal (x:-1|1) in Landscape mode, this can be skipped. Such bubble will always expend in its facing direction.
             */
            contentGrowthX?: number;
        };
    };
}

export type ReorderCardsProps = {
    landscape: ReorderCardsOrientationProps;
    portrait: ReorderCardsOrientationProps;
}

export type ReorderCardsOrientationProps = {
    cardsSpacing: number;
    cardAngleDeviance: number;
    cardsCircleRadius: number;
    cardsCirclePosition: Phaser.Geom.Point;
    /**
     * Playable/Popped cards - ONLY when 'popupPlayableCards' is selected in options.
     */
    poppedCardsCirclePosition: Phaser.Geom.Point;
}

export type SerializableAvatarProps = {
    /**
     * Cached localized avatar name (Player uses "Player", which is not used in avatar label display, but is used in some screens)
     */
    name: string;
    /**
     * Avatar image as frame from avatars atlas.
     */
    frame: string;
}

export class Players
    implements IShowable {
    public static PLAYER_INDEX: number = 2;
    public static PLAYER_NAME: string = "${PLAYER_NAME}";
    private scene: Phaser.Scene;
    private table: TableScreen;

    /**
     * Hands hold cards. These are players' hands.
     * Top, Right, Bottom, Left
     * Player = Bottom (index 2)
     */
    private players: Player[];

    private playersProps: PlayerProps[];
    /**
     * These properties handle spacing and angle of cards in differnet orientations
     * NOTE: scaling is handled on table level @see Table::OnResize->players.OnResize
     */
    private reorderProps: ReorderCardsProps[];

    private isShowing: boolean = false;


    public Init(scene: Phaser.Scene, table: TableScreen): void {
        this.scene = scene;
        this.table = table;

        const botCardsFrontFacing: boolean = AppDebug.IsShowCards() ? true : false;
        const playerCardsFrontFacing: boolean = true;
        this.players = [];
        /**
         * NOTE: Bot avatar scale is always 1. only container scales (container contains avatar, avatarBg, label, lbaelBg). This way we unsure label/labelBg scales proportionally to avatar.
         */
        this.playersProps = [
            // North
            {
                dX: 0.5,
                dY: 0.0,
                facing: new Phaser.Geom.Point(0, 1),
                cardsContainer: {
                    portrait: {
                        scale: 0.6,
                        bias: 0.9,
                        screenEdgeOffset: 40,
                    },
                    landscape: {
                        scale: 0.4,
                        bias: 0.9,
                        screenEdgeOffset: 40,
                    },
                    frontFacing: botCardsFrontFacing
                },
                avatarContainer: {
                    portrait: {
                        scale: 1.8,
                        bias: 0.9,
                        screenEdgeOffset: 10,
                    },
                    landscape: {
                        scale: 1.2,
                        bias: 0.9,
                        screenEdgeOffset: 10,
                    },
                },
                avatar: {
                    portrait: {
                        scale: 1.0,
                    },
                    landscape: {
                        scale: 1.0,
                    },
                },
                // NOTE: North
                speechBubble: {
                    landscape: {
                        facing: new Phaser.Geom.Point(1, 0), // facing from left to right
                        contentGrowthX: 1, // in this case this can be skipped
                    },
                    portrait: {
                        facing: new Phaser.Geom.Point(0, 1), // facing from top to bottom
                        contentGrowthX: 0, // align to center horizontally
                    },
                },
            },
            // East
            {
                dX: 1.0,
                dY: 0.5,
                facing: new Phaser.Geom.Point(-1, 0),
                cardsContainer: {
                    portrait: {
                        scale: 0.6,
                        bias: 0.9,
                        screenEdgeOffset: 40,
                    },
                    landscape: {
                        scale: 0.4,
                        bias: 0.9,
                        screenEdgeOffset: 40,
                    },
                    frontFacing: botCardsFrontFacing
                },
                avatarContainer: {
                    portrait: {
                        scale: 1.8,
                        bias: 0.9,
                        screenEdgeOffset: 10,
                    },
                    landscape: {
                        scale: 1.2,
                        bias: 0.9,
                        screenEdgeOffset: 10,
                    },
                },
                avatar: {
                    portrait: {
                        scale: 1.0,
                    },
                    landscape: {
                        scale: 1.0,
                    },
                },
                // NOTE: East
                speechBubble: {
                    landscape: {
                        facing: new Phaser.Geom.Point(-1, 0),
                        contentGrowthX: -1,
                    },
                    portrait: {
                        facing: new Phaser.Geom.Point(0, -1),
                        contentGrowthX: -1,
                    },
                },
            },
            // South (Player)
            {
                dX: 0.5,
                dY: 1.0,
                facing: new Phaser.Geom.Point(0, -1),
                cardsContainer: {
                    portrait: {
                        scale: 1.9,
                        bias: 1.0,
                        screenEdgeOffset: 60,
                    },
                    landscape: {
                        scale: 0.9,
                        bias: 0.9,
                        screenEdgeOffset: 80,
                    },
                    frontFacing: playerCardsFrontFacing
                },
                avatarContainer: {
                    portrait: {
                        scale: 3.4,
                        bias: 1.0,
                        screenEdgeOffset: 10,
                    },
                    landscape: {
                        scale: 1.3,
                        bias: 0.9,
                        screenEdgeOffset: 10,
                    },
                },
                avatar: {
                    portrait: {
                        scale: 0.8,
                    },
                    landscape: {
                        scale: 1.2,
                    },
                },
                acceptorBubble: {
                    portrait: {
                        scale: 0.55,
                    },
                    landscape: {
                        scale: 0.8,
                    },
                },
                // NOTE: South
                speechBubble: {
                    landscape: {
                        facing: new Phaser.Geom.Point(1, 0),
                        contentGrowthX: 1,
                    },
                    portrait: {
                        facing: new Phaser.Geom.Point(1, 0),
                        contentGrowthX: 1,
                    },
                },
            },
            // West
            {
                dX: 0.0,
                dY: 0.5,
                facing: new Phaser.Geom.Point(1, 0),
                cardsContainer: {
                    portrait: {
                        scale: 0.6,
                        bias: 0.9,
                        screenEdgeOffset: 40,
                    },
                    landscape: {
                        scale: 0.4,
                        bias: 0.9,
                        screenEdgeOffset: 40,
                    },
                    frontFacing: botCardsFrontFacing
                },
                avatarContainer: {
                    portrait: {
                        scale: 1.8,
                        bias: 0.9,
                        screenEdgeOffset: 10,
                    },
                    landscape: {
                        scale: 1.2,
                        bias: 0.9,
                        screenEdgeOffset: 10,
                    },
                },
                avatar: {
                    portrait: {
                        scale: 1.0,
                    },
                    landscape: {
                        scale: 1.0,
                    },
                },
                // NOTE: West
                speechBubble: {
                    landscape: {
                        facing: new Phaser.Geom.Point(1, 0),
                        contentGrowthX: 1,
                    },
                    portrait: {
                        facing: new Phaser.Geom.Point(0, -1),
                        contentGrowthX: 1,
                    },
                },
            },
        ];

        const defaultPortraitReorderProps = {
            cardsSpacing: 2.7,
            cardAngleDeviance: 0.8,
            cardsCircleRadius: 2000,
            cardsCirclePosition: new Phaser.Geom.Point(0, 1960),
            poppedCardsCirclePosition: new Phaser.Geom.Point(0, 1900),
        }
        const defaultLandscapeReorderProps = {
            cardsSpacing: 0.12,
            cardAngleDeviance: 0.0,
            cardsCircleRadius: 80000,
            cardsCirclePosition: new Phaser.Geom.Point(0, 79960),
            poppedCardsCirclePosition: new Phaser.Geom.Point(0, 79900),
        }
        this.reorderProps = [
            // North
            {
                portrait: defaultPortraitReorderProps,
                landscape: defaultPortraitReorderProps,
            },
            // East
            {
                portrait: defaultPortraitReorderProps,
                landscape: defaultPortraitReorderProps,
            },
            // South
            {
                portrait: defaultPortraitReorderProps,
                landscape: defaultLandscapeReorderProps,
            },
            // West
            {
                portrait: defaultPortraitReorderProps,
                landscape: defaultPortraitReorderProps,
            }
        ];
    }

    public Build(): void {
        for (let i: number = 0; i < this.playersProps.length; i++) {
            const playerProps: PlayerProps = this.playersProps[i];
            // const playerAvatarProps: SerializableAvatarProps = 
            const player: Player = new Player(this.scene);
            player.index = i;
            player.Init(
                this.table,
                // NOTE: set via @see this.SetAvatarsProps via statemachine (either from cache or generates new)
                { texture: LoaderFileKeys.avatars, frame: null },
                playerProps,
                this.reorderProps[i],
                i == Players.PLAYER_INDEX // isPlayer?
            );
            player.Build();
            this.players.push(player);
            this.table.add(player);

            // alpha transparent player is showCards debug mode on
            if (AppDebug.IsShowCards()) {
                if (i != Players.PLAYER_INDEX) {
                    player.avatar.alpha =
                        player.chipContainer.alpha =
                        player.avatarBg.alpha =
                        player.nsLabelBg.alpha =
                        player.labelText.alpha = 0;
                }
            }
        }
    }

    /**
     * 
     * @param animated 
     * @param onComplete 
     * @param triggerResize Settings to TRUE causes a single RESIZE refresh before Show(). 
     *      Necessary to call with true sometimes, since we dont process RESIZE triggers 
     *      internally unless isShow=true.
     */
    public Show(animated: boolean = true, onComplete: Function = undefined, triggerResize: boolean = false): void {
        for (let i: number = 0; i < this.players.length; i++) {
            const player: Player = this.players[i];
            player.Show(animated, i == this.players.length - 1 ? onComplete : undefined, triggerResize);
        }
        this.isShowing = true;
    }

    public Hide(animated: boolean = true, onComplete: Function = undefined): void {
        for (let i: number = 0; i < this.players.length; i++) {
            const player: Player = this.players[i];
            player.Hide(animated, i == this.players.length - 1 ? onComplete : undefined);
        }
        this.isShowing = false;
    }

    public IsShowing(): boolean {
        return this.isShowing;
    }

    /**
     * Put cards back to CardsManager
     * Part of @see Reset
     */
    public Reset(): void {
        this.players.forEach(player => player.Reset());
    }

    public GetLength(): number {
        return this.players.length;
    }

    /**
     * Generate new player names based on langauge and puts them into @see this.playersNames
     * @param langId Optional language: generate for this langauge, or use 'current language' in AppLocalization.
     */
    public GenerateAvatarsProps(langId: LanguageId = undefined): SerializableAvatarProps[] {
        // current language id set in AppLocalization or supplied one
        if (langId == undefined) {
            langId = AppLocalization.GetCurLanguageId();
        }
        const locNamesMale: string[] = AppLocalization.GetSharedData().general.playerBotNamesMale;
        const locNamesFemale: string[] = AppLocalization.GetSharedData().general.playerBotNamesFemale;
        const avatarFramesMale: string[] = LayoutDataManager.GetSharedData().general.playerAvatarFramesMale;
        const avatarFramesFemale: string[] = LayoutDataManager.GetSharedData().general.playerAvatarFramesFemale;
        // random number of males and females for bots
        const femaleCount: number = Phaser.Math.Between(0, 3);
        const maleCount: number = 3 - femaleCount;
        const femaleNamesIndexes: number[] = GetUniqueNumbersFromInterval(0, locNamesFemale.length - 1, femaleCount);
        const maleNamesIndexes: number[] = GetUniqueNumbersFromInterval(0, locNamesMale.length - 1, maleCount);
        const femaleAvatarsFrameIndexes: number[] = GetUniqueNumbersFromInterval(0, avatarFramesFemale.length - 1, femaleCount);
        const maleAvatarsFrameIndexes: number[] = GetUniqueNumbersFromInterval(0, avatarFramesMale.length - 1, maleCount);
        let botProps: SerializableAvatarProps[] = [];
        
        for (let i = 0; i < femaleCount; i++) {
            const femaleNameIndex = femaleNamesIndexes[i];
            const femaleAvatarFrameIndex = femaleAvatarsFrameIndexes[i];
            // botNamesFemale.push(locNamesFemale[femaleNameIndex]);
            botProps.push({
                name: locNamesFemale[femaleNameIndex],
                frame: avatarFramesFemale[femaleAvatarFrameIndex],
            });
        }
        
        for (let i = 0; i < maleCount; i++) {
            const maleNameIndex = maleNamesIndexes[i];
            const maleAvatarFrameIndex = maleAvatarsFrameIndexes[i];
            botProps.push({
                name: locNamesMale[maleNameIndex],
                frame: avatarFramesMale[maleAvatarFrameIndex],
            });
        }
        Phaser.Utils.Array.Shuffle(botProps); // modified directly and returned

        // fill playersNames
        const avatarsProps: SerializableAvatarProps[] = [
            botProps.pop(),
            botProps.pop(),
            {
                // name: AppLocalization.GetSharedData(langId).general.playerName,
                name: Players.PLAYER_NAME,
                frame: LayoutDataManager.GetSharedData().general.playerAvatarFramesUnisex[Phaser.Math.Between(0, LayoutDataManager.GetSharedData().general.playerAvatarFramesUnisex.length - 1)],
            },
            botProps.pop(),
        ];

        return avatarsProps;
    }

    public GetPlayers(): Player[] {
        return this.players;
    }

    public HideBubbles(animate: boolean = true): void {
        this.players.forEach(player => {
            if (player.speechBubble.isShowing) {
                player.speechBubble.Hide(animate);
            }
        });
    }

    public GetPlayersCardDatas(): CardData[][] {
        return this.players.map((player: Player) => {
            return player.GetCardDatas();
        });
    }

    /**
     * Generates new (in language) or sets supplied playersNames to Players.
     * 
     * In detail, assigns:
     * (1) From @see playersNames
     * - localization has no effect, FULL player/bot names are provided (as loaded from session cache)
     * (2) Generated from random collections:
     * - based on localization file language (shared by all variations)
     * - this is why we save the names (if player changes language, original names stay)
     * - 'Player' name is localized, but not generated from collection.
     * 
     * @param playersNames assign these names. Most prob. came from saved session data.
     * @param langId language used to generate new names (if no names were provided in @see playersNames)
     */
    public SetAvatarsProps(avatarsProps: SerializableAvatarProps[] = undefined, langId: LanguageId = undefined): void {
        // current language id set in AppLocalization or supplied one
        if (langId == undefined) {
            langId = AppLocalization.GetCurLanguageId();
        }
        // 
        if (avatarsProps == undefined) {
            avatarsProps = this.GenerateAvatarsProps(langId);
        }
        // assign to views
        this.players.forEach((player: Player, i: number) => {
            player.SetAvatarFrame(avatarsProps[i].frame);
            let playerName = avatarsProps[i].name;
            // playerName = playerName.replace(Players.PLAYER_NAME, AppLocalization.GetSharedData(langId).general.playerName)
            // console.log(playerName);
            player.SetName(playerName);
        });
    }

    public GetPlayerByIndex(playerIndex: number): Player {
        return this.players[playerIndex];
    }

    /**
     * Controlled through parent
     * @param windowRect 
     */
    public OnResize(windowRect: RectSize): void {
        // console.log("Players::OnResize");
        // console.log(windowRect.width, windowRect.height);

        // based on orientation
        for (let i: number = 0; i < this.players.length; i++) {
            const player: Player = this.players[i];
            const playerProps: PlayerProps = player.GetPlayerProps();

            // scale first
            player.OnResize();

            // position second
            player.setPosition(
                (playerProps.dX * windowRect.width) | 0,
                (playerProps.dY * windowRect.height) | 0
            );
        }
    }

    /**
     * Affects: Player::OnOrientationChanged->card spacing and angle on Player cards (horizontal is bigger spacing and smaller angle/bigger circle radius)
     */
    public OnOrientationChanged(screenOrientationType: ScreenOrientationType): void {
        // switch (screenOrientationType) {
        //     case ScreenOrientationType.LANDSCAPE:
        //         break;
        //     case ScreenOrientationType.PORTRAIT:
        //         break;
        // }
        this.players[Players.PLAYER_INDEX].OnOrientationChanged(screenOrientationType);
    }
}